import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Badge,
  Button,
  Typography,
  Chip,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
  Tooltip,
  keyframes,
} from "@mui/material";
import { Menu as MenuIcon, Assignment as AssignmentIcon } from "@mui/icons-material";
import { useColorMode } from "../../context/ColorModeContext";
import { useLayout } from "../../context/LayoutContext";
import LargeLogoWithTextDark from "../../assets/Lockup Horizontal Dark.svg";
import LargeLogoWithTextLight from "../../assets/Lockup Horizontal Light.svg";
import { useNavigate, useLocation } from "react-router-dom";

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

// Three dots that bounce in sequence (staggered animation-delay) to form a
// wave — the loading indicator shown in place of a not-yet-known count.
const waveAnimation = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.45;
  }
  30% {
    transform: translateY(-3px);
    opacity: 1;
  }
`;

interface TopAppBarProps {
  onMenuClick: () => void;
  onReportTrackerClick: () => void;
  reportTrackerOpen: boolean;
  jobCount: number;
  isPulsating: boolean;
  getBadgeColor: () => "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  sidebarOpen?: boolean;
}

const TopAppBar: React.FC<TopAppBarProps> = ({
  onMenuClick,
  onReportTrackerClick,
  reportTrackerOpen,
  jobCount,
  isPulsating,
  getBadgeColor,
}) => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const { pageHeader, subNavItems } = useLayout();
  const { mode } = useColorMode();
  const currentPath = location.pathname.replace(/\/$/, "");

  const handleLogoClick = () => {
    navigate("/");
  };

  // Haptic feedback helper for touch devices
  const triggerHaptic = (pattern: number | number[]) => {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  };

  const handleReportTrackerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Trigger haptic feedback on click
    triggerHaptic([15, 30, 15]); // Quick double-tap pattern
    onReportTrackerClick();
    e.currentTarget.blur();
  };

  const handleReportTrackerTouchStart = () => {
    // Light haptic on touch start for immediate feedback
    triggerHaptic(10);
  };

  const handleReportTrackerMouseDown = () => {
    // Slightly stronger haptic on press
    triggerHaptic(25);
  };

  // Keep branding width constant to ensure logo + text is always visible
  const brandingWidth = 240;

  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={1}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        bgcolor: "background.paper",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar sx={{ pl: { lg: 0 } }}>
        {/* Menu icon - only visible on mobile */}
        {!isLgUp && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logo - hidden on xs screens to prevent clipping */}
        {isSmUp && (
          <Box
            onClick={handleLogoClick}
            sx={{
              width: isLgUp ? brandingWidth : "auto",
              pl: { lg: 3 }, // Restore visual padding inside container (24px)
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              overflow: "hidden", // Ensures logo clips cleanly when collapsed
              transition: "width 0.2s ease-in-out",
              cursor: "pointer",
            }}
          >
            <Box
              component="img"
              sx={{
                height: isLgUp ? 32 : 24,
                width: "auto",
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
              alt="Logo"
              src={mode === 'dark' ? LargeLogoWithTextLight : LargeLogoWithTextDark}
            />
          </Box>
        )}

        {/* Page Header or Spacer */}
        <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", ml: 2 }}>
          {pageHeader && (
            <>
              {/* Single-line header: the title owns the line at full size;
                  the subtitle sits beside it and is the part that shrinks
                  and ellipsizes when space runs out. The icon lives OUTSIDE
                  the text row: it is vertically centered, while title and
                  subtitle align on their shared text baseline — flex
                  baseline alignment only works when every item in the row
                  is plain text. */}
              <Box
                sx={{
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {pageHeader.icon && (
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: "primary.main",
                      "& > svg": { fontSize: 22 },
                    }}
                  >
                    {pageHeader.icon}
                  </Box>
                )}
                <Box
                  sx={{
                    minWidth: 0,
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1.5,
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      fontSize: "1.15rem",
                      lineHeight: 1.3,
                      color: "text.primary",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {pageHeader.title}
                  </Typography>
                  {pageHeader.subtitle && isLgUp && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                      }}
                    >
                      {pageHeader.subtitle}
                    </Typography>
                  )}
                </Box>
              </Box>
              {pageHeader.chip && (
                <Chip
                  label={pageHeader.chip.loading ? (
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                      <Box
                        component="span"
                        aria-hidden="true"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "2px",
                          mr: 0.75,
                          "& > span": {
                            width: "3px",
                            height: "3px",
                            borderRadius: "50%",
                            backgroundColor: "currentColor",
                            animation: `${waveAnimation} 1.2s ease-in-out infinite`,
                          },
                          "& > span:nth-of-type(2)": { animationDelay: "0.15s" },
                          "& > span:nth-of-type(3)": { animationDelay: "0.3s" },
                        }}
                      >
                        <span />
                        <span />
                        <span />
                      </Box>
                      {pageHeader.chip.label}
                    </Box>
                  ) : (
                    pageHeader.chip.label
                  )}
                  icon={!pageHeader.chip.loading && pageHeader.chip.icon ? (
                    <Box component="span" sx={{ display: "flex", alignItems: "center", "& > svg": { fontSize: 16 } }}>
                      {pageHeader.chip.icon}
                    </Box>
                  ) : undefined}
                  size="small"
                  sx={{
                    ml: 2,
                    fontWeight: 600,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    "& .MuiChip-icon": {
                      color: "inherit",
                    },
                  }}
                />
              )}
            </>
          )}
        </Box>

        {/* Sub-navigation tabs */}
        {subNavItems.length > 0 && (
          <Tabs
            value={
              subNavItems.findIndex(
                (item) => currentPath === item.path.replace(/\/$/, "")
              ) === -1
                ? false
                : subNavItems.findIndex(
                    (item) => currentPath === item.path.replace(/\/$/, "")
                  )
            }
            onChange={(_, newValue) => navigate(subNavItems[newValue].path)}
            sx={{
              minHeight: 48,
              ml: 2,
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.875rem",
                minHeight: 48,
                px: 2,
                gap: 0.75,
                "&.Mui-selected": {
                  fontWeight: 600,
                },
              },
            }}
          >
            {subNavItems.map((item) => (
              <Tab
                key={item.path}
                label={item.label}
                icon={
                  item.icon ? (
                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        "& > svg": { fontSize: 18 },
                      }}
                    >
                      {item.icon}
                    </Box>
                  ) : undefined
                }
                iconPosition="start"
              />
            ))}
          </Tabs>
        )}

        {/* Report Tracker Button */}
        <Tooltip
          title={reportTrackerOpen ? "" : "View your Report Tracker"}
          placement="bottom"
          arrow
          enterDelay={300}
        >
          <Button
            onClick={handleReportTrackerClick}
            onTouchStart={handleReportTrackerTouchStart}
            onMouseDown={handleReportTrackerMouseDown}
            variant="contained"
            startIcon={
              <Badge
                badgeContent={jobCount}
                color={getBadgeColor()}
                invisible={jobCount === 0}
                sx={{
                  "& .MuiBadge-badge": {
                    fontWeight: "bold",
                    fontSize: "0.7rem",
                    border: `2px solid ${theme.palette.background.paper}`,
                  },
                }}
              >
                <AssignmentIcon />
              </Badge>
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: 2,
              py: 0.75,
              ml: 2,
              transition: "all 0.2s ease-in-out",
              boxShadow: "none",
              flexShrink: 0,
              color: theme.palette.gradient.onBrand,
              background: theme.palette.gradient.brand,
              ...(isPulsating && {
                animation: `${pulseAnimation} 1.5s infinite`,
              }),
              "&:hover": {
                transform: "scale(1.02)",
                boxShadow: theme.shadows[2],
                background: theme.palette.gradient.brandHover,
              },
            }}
          >
            {isLgUp ? "Report Tracker" : "Tracker"}
          </Button>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
};

export default TopAppBar;
